import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { ADVANCE_WMS_MODULE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const VendorForm = NiceModal.create(({ data, mode, modalName }) => {
	const modal = useModal();
	const schema = useMemo(
		() =>
			yup.object().shape({
				vendorName: yup.string().nullable().required(),
				email: yup.string().nullable().required(),
				accountNumber: yup.string().nullable().required()
			}),
		[]
	);
	const [loading, setLoading] = useState(false);

	const viewMode = mode === 'view';

	const dispatch = useDispatch();
	const axios = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();

	// getters
	const getTitle = () => (mode === 'edit' ? 'editVendor' : mode === 'view' ? 'viewVendor' : 'addVendor');
	const getSubmitLabel = () => (mode === 'edit' ? 'save' : mode === 'view' ? 'button.view' : 'button.add');

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: data ?? {
			vendorName: null,
			email: null,
			accountNumber: null
		}
	});
	const {
		handleSubmit,
		reset,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);
	const defaultColSpan = {
		md: 6
	};
	const onSubmit = async (submitData) => {
		setLoading(true);

		const URL = data ? ADVANCE_WMS_MODULE.UPDATE_VENDOR : ADVANCE_WMS_MODULE.ADD_VENDOR;

		const body = data ? { ...submitData, ID: parseInt(data.ID) } : { ...submitData };
		try {
			const { status } = await axios.post(URL, body);
			if (status === 200) {
				showSuccess();
				dispatch(updateDynamicListData(DYNAMICLISTS.VENDORS));
				modal.remove();
			}
		} catch (error) {
			if (error.response?.data?.code === 'error.F010') {
				showError({
					message: 'error.F010'
				});
			}
			AppLogger.error(error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		data && reset(data);
	}, [data]);

	return (
		<Modal name={modalName} maxWidth="small" fullwidth>
			<ModalTitle>
				<Typography variant="p1">{getTitle()}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="vendorName" isLoading={loading} required>
						<StringInput disabled={viewMode} name="vendorName" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="email" isLoading={loading} required>
						<StringInput disabled={viewMode} name="email" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="accountNumber" isLoading={loading} required>
						<StringInput disabled={viewMode} name="accountNumber" />
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={() => modal.remove()} />
				{!viewMode && (
					<Button
						label={getSubmitLabel()}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						loading={loading}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
					/>
				)}
			</ModalActions>
		</Modal>
	);
});
export default VendorForm;
